import {
  defineMetamodelPackage,
  type EffectMetamodelManifestSpec,
  type MetamodelGraphqlMutation,
} from "@granular-software/metamodel-core";
import {
  normalizeCondition,
  summarizeCondition,
} from "@granular-software/policy-engine";

function escapeGraphqlString(value: string): string {
  return JSON.stringify(value);
}

function buildPolicyMutations(
  effectKey: string,
  spec: EffectMetamodelManifestSpec,
): MetamodelGraphqlMutation[] {
  const policies = spec.policies;
  if (!policies) return [];

  const mutations: MetamodelGraphqlMutation[] = [];
  const addRules = (
    key: "allowWhen" | "confirmWhen" | "denyWhen",
    outcome: "allow" | "confirm" | "deny",
  ) => {
    const rules = policies[key] || [];
    rules.forEach((rule, index) => {
      const condition = normalizeCondition(rule.when);
      const summary = rule.reason || summarizeCondition(condition);
      const id = rule.id || `${effectKey}:${outcome}:${index + 1}`;
      mutations.push({
        label: `set policy ${outcome} on ${effectKey}`,
        query: `mutation { set_policy_rule(effect_key: ${escapeGraphqlString(effectKey)}, policy_id: ${escapeGraphqlString(id)}, outcome: ${escapeGraphqlString(outcome)}, reason: ${escapeGraphqlString(summary)}, condition_json: ${escapeGraphqlString(JSON.stringify(condition))}) }`,
      });
    });
  };

  addRules("allowWhen", "allow");
  addRules("confirmWhen", "confirm");
  addRules("denyWhen", "deny");
  return mutations;
}

export const policyMetamodelPackage = defineMetamodelPackage({
  id: "policy",
  manifest: {
    buildEffectMutations: buildPolicyMutations,
  },
  summary: {
    selections: {
      methodFields: ["policies"],
    },
    readMethodSummary(rawMethod) {
      return rawMethod.policies
        ? { metamodels: { policies: rawMethod.policies } }
        : {};
    },
  },
  docs: {
    effectRows: [
      {
        key: "policies",
        description:
          "Universal effect policies with allowWhen, confirmWhen, and denyWhen structural conditions.",
      },
    ],
  },
});
